import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogProps, DialogType, DialogWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { isNullOrUndefined } from 'util';

import { getAndOpenLinkedTaskDetailsDialog } from '~src/linkedTasks/components/LinkedTaskDetailsDialog';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';

import { Messages } from '../../codegen/Localize';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { BackgroundJobDefinition } from '../../contentDefinitions/BackgroundJobDefinition';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IStringValuedFilterClause } from '../../ts/ServerApi/types/IFilterClause';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { IBackgroundJobInfo } from '../types/BackgroundJobs';
import { GetBackgroundJobExtendedInfoRequest } from '../types/GetBackgroundJobExtendedInfoRequest';
import { IBackgroundJob } from '../types/IBackgroundJob';
import { getBackgroundJobExtendedInfoDialog, IBackgroundJobExtendedInfo } from '../types/IBackgroundJobExtendedInfo';


export interface BackgroundJobExtendedInfoDialogProps {
  backgroundJob: IBackgroundJob;
  data: IBackgroundJobExtendedInfo;
  text: string;
  title: string;
  testId: string;
  isServerPage: boolean;
  close: () => void;
}

export interface BackgroundJobExtendedInfoDialogInstance {
  backgroundJob: IBackgroundJobInfo;
  extendedInfo: IBackgroundJobExtendedInfo;
  close: () => void;
}

// The reference to this immutable array gets overwritten as dialogs are open or closed
let dialogInstances: ReadonlyArray<BackgroundJobExtendedInfoDialogInstance> = [];

export class BackgroundJobExtendedInfoDialog extends React.PureComponent<BackgroundJobExtendedInfoDialogProps> {

  public static toggle(
    backgroundJob: IBackgroundJobInfo
  ) {
    const existingInstance = dialogInstances.find(instance => instance.backgroundJob.jobId === backgroundJob.jobId);
    if (existingInstance) {
      existingInstance.close();
    } else {
      BackgroundJobExtendedInfoDialog.open(backgroundJob);
    }
  }

  private static async open(
    backgroundJob: IBackgroundJobInfo
  ) {
    const request = new GetBackgroundJobExtendedInfoRequest({ backgroundJobId: backgroundJob.jobId + '' });
    const extendedInfo = await WebHttpClientWithErrorToastInstance.sendRequest(request);

    if (extendedInfo.errors && extendedInfo.errors.length > 0) {
      const service = ToasterService.instance;
      service.pop({
        text: Messages.backgroundJobsExtendedInfoError(),
        type: ToastType.Error,
      });

      // Failed to open the dialog: bail
      return;
    }

    const siteIdFilter = BackgroundJobDefinition.defaultFilters('').siteId;
    const isServerPage = isNullOrUndefined(siteIdFilter) || (siteIdFilter as IStringValuedFilterClause).value === undefined;

    let closeHandler = () => {
      dialogInstances = dialogInstances.filter(instance => instance.backgroundJob.jobId !== backgroundJob.jobId);
    };

    const modalInstance = ReactModalService.openCustom(({ close }) => (
      <BackgroundJobExtendedInfoDialog
        text={backgroundJob.jobId + ''}
        title={Messages.backgroundJobsExtendedInfoTitle({ ID: backgroundJob.jobId + '' })}
        testId={'background-job-extended-info-' + backgroundJob.jobId}
        isServerPage={isServerPage}
        close={close}
        backgroundJob={backgroundJob}
        data={extendedInfo}
      />
    ), closeHandler);

    dialogInstances = dialogInstances.concat({
      backgroundJob,
      extendedInfo,
      close: modalInstance.close,
    });
  }


  constructor(props: BackgroundJobExtendedInfoDialogProps) {
    super(props);
    this.state = {};
  }

  render() {
    const dialogProps: DialogProps = {
      type: DialogType.NormalWithTitleBar,
      title: this.props.title,
      testId: this.props.testId,
      shouldReturnFocusOnClose: false,
      dialogWidth: 375,
      isModal: false,
      showCloseButton: true,
      onCloseRequest: this.props.close,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      showFooter: true,
      footerContent: <DialogFooterButtonsWidget
        buttons={[{
          handleClick: () => {
            this.props.close();
          },
          buttonType: ButtonType.Go,
          children: Messages.backgroundJobsExtendedInfoOK(),
          testId: 'footer-confirm'
        }]}
        testId="footer"
      />,
    };

    const isLinkedTaskJob = areLinkedTasksEnabled() && this.props.data.linkedTaskId !== undefined &&
      this.props.data.linkedTaskStepNumber !== undefined && this.props.data.linkedTaskTotalSteps !== undefined;
    const linkedTaskProps = {
      linkedTaskId: this.props.data.linkedTaskId,
      linkedTaskStepNumber: this.props.data.linkedTaskStepNumber,
      linkedTaskTotalSteps: this.props.data.linkedTaskTotalSteps,
    };
    function handleLinkedTaskClick() {
      getAndOpenLinkedTaskDetailsDialog(linkedTaskProps.linkedTaskId);
    }
    const contentName = Messages.backgroundJobsExtendedInfoContent({
      CONTENT_NAME: this.props.data.contentName,
    });

    return <DialogWidget {...dialogProps}>
      {getBackgroundJobExtendedInfoDialog(this.props.backgroundJob, this.props.data, this.props.isServerPage).map((line, i) =>
        (line === contentName) && isLinkedTaskJob ?
          <p
            key={'background-job-extended-info-dialog-line-' + i}
            style={{ margin: '0' }}
          >
            {Messages.backgroundJobsExtendedInfoContentLinkedTask()}
            <TextLinkWidget
              testId={'BackgroundJobExtendedInfoLinkedTaskTextLink'}
              onClick={handleLinkedTaskClick}
            >
              {this.props.data.contentName}
            </TextLinkWidget>
            {Messages.backgroundJobsExtendedInfoLinkedTaskSteps({
              LINKEDTASK_STEP_NUM: linkedTaskProps.linkedTaskStepNumber,
              LINKEDTASK_TOTAL_STEPS: linkedTaskProps.linkedTaskTotalSteps,
            })}
          </p> :
          <p
            key={'background-job-extended-info-dialog-line-' + i}
            style={{ margin: '0' }}
          >
            {line}
          </p>
      )}
    </DialogWidget>;
  }
}
